import React, { useEffect, useRef, useState } from "react";
import {
  EllipsisVertical,
  Loader2,
  Pencil,
  Trash,
  type LucideIcon,
} from "lucide-react";

interface InfoCardProps {
  label: string;
  value: string;
  icon: LucideIcon;
  color: "blue" | "green" | "red" | "orange" | "purple";
  editable?: boolean;
  deletable?: boolean;
  actionable?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  onAction?: () => void;
  isLoading?: boolean;
}

export default function InfoCard({
  label,
  value,
  icon,
  color,
  editable = false,
  deletable = false,
  actionable = false,
  onEdit = () => {},
  onDelete = () => {},
  onAction = () => {},
  isLoading = false,
}: InfoCardProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const showMenu = editable || deletable;

  const hoverClasses: Record<string, string> = {
    blue: "hover:bg-blue-50 hover:ring-1 hover:ring-blue-500",
    green: "hover:bg-green-50 hover:ring-1 hover:ring-green-500",
    red: "hover:bg-red-50 hover:ring-1 hover:ring-red-500",
    orange: "hover:bg-orange-50 hover:ring-1 hover:ring-orange-500",
    purple: "hover:bg-purple-50 hover:ring-1 hover:ring-purple-500",
  };

  const bgClasses: Record<string, string> = {
    blue: "bg-blue-50",
    green: "bg-green-50",
    red: "bg-red-50",
    orange: "bg-orange-50",
    purple: "bg-purple-50",
  };

  const textClasses: Record<string, string> = {
    blue: "text-blue-500",
    green: "text-green-500",
    red: "text-red-500",
    orange: "text-orange-500",
    purple: "text-purple-500",
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div
      className={`flex gap-4 items-center border border-gray-200 rounded-lg p-4 transition-all duration-300 ${
        !showMenu ? `cursor-pointer ${hoverClasses[color]}` : ""
      }`}
      onClick={() => {
        if (showMenu) {
          setIsMenuOpen(!isMenuOpen);
        } else if (actionable) {
          onAction();
        }
      }}
    >
      <div
        className={`p-4 ${bgClasses[color]} rounded-lg flex items-center justify-center`}
      >
        {isLoading ? (
          <Loader2 className={`w-8 h-8 ${textClasses[color]} animate-spin`} />
        ) : (
          React.createElement(icon, {
            className: `w-8 h-8 ${textClasses[color]}`,
          })
        )}
      </div>
      <div className="flex flex-col justify-center gap-2 w-full">
        <div className="flex w-full items-center justify-between">
          {!actionable && <p className="text-sm text-gray-500">{label}</p>}
          {(editable || deletable) && !actionable && (
            <div
              ref={menuRef}
              className="relative hover:bg-gray-50 rounded-full p-2 cursor-pointer"
            >
              <EllipsisVertical
                className="w-4 h-4"
                onClick={() => {
                  console.log("clicked");
                  setIsMenuOpen(!isMenuOpen);
                }}
              />
              {isMenuOpen && (
                <div className="absolute right-0 mt-2 w-32 bg-white border rounded-md shadow-lg z-10">
                  <button
                    onClick={onEdit}
                    className=" w-full text-left px-4 py-2 flex items-center gap-2 hover:bg-yellow-50 cursor-pointer"
                  >
                    <div className="flex items-center justify-center bg-yellow-50  rounded-full p-2">
                      <Pencil className="w-4 h-4 text-yellow-500" />
                    </div>
                    <span className="text-sm font-semibold text-yellow-500">
                      Edit
                    </span>
                  </button>
                  <button
                    onClick={onDelete}
                    className="flex w-full text-left px-4 py-2 hover:bg-red-50 transition-all duration-300 text-red-500 items-center gap-2 cursor-pointer"
                  >
                    <div className="flex items-center justify-center bg-red-50 rounded-full p-2">
                      <Trash className="w-4 h-4" />
                    </div>
                    <span className="text-sm font-semibold text-red-500">
                      Delete
                    </span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        <p className="font-semibold">{value}</p>
      </div>
    </div>
  );
}
